import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const BulkActionsToolbar = ({ selectedCount, selectedPropertyIds = [], onBulkAction, className = "" }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const bulkActionOptions = [
    { value: '', label: 'Select bulk action...' },
    { value: 'schedule-survey', label: 'Schedule Survey' },
    { value: 'update-status', label: 'Update Status' },
    { value: 'assign-surveyor', label: 'Assign Surveyor' },
    { value: 'export-data', label: 'Export Data' },
    { value: 'generate-reports', label: 'Generate Reports' },
    { value: 'send-notifications', label: 'Send Notifications' },
    { value: 'archive', label: 'Archive Properties' },
    { value: 'delete', label: 'Delete Properties' }
  ];

  const handleBulkAction = async () => {
    if (!selectedAction || selectedCount === 0) return;
    setIsProcessing(true);
    onBulkAction(selectedAction, selectedPropertyIds);
    setSelectedAction('');
    setIsProcessing(false);
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'schedule-survey': 'Calendar',
      'update-status': 'RefreshCw',
      'assign-surveyor': 'UserCheck',
      'export-data': 'Download',
      'generate-reports': 'FileText',
      'send-notifications': 'Bell',
      'archive': 'Archive',
      'delete': 'Trash2'
    };
    return iconMap?.[action] || 'Settings';
  };

  const getActionVariant = (action) => {
    if (action === 'delete') return 'destructive';
    if (action === 'archive') return 'warning';
    return 'default';
  };

  if (selectedCount === 0) return null;

  return (
    <div className={`bg-card border border-border rounded-lg p-4 shadow-elevation-1 ${className}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Selection Info */}
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-medium">
            {selectedCount}
          </div>
          <div>
            <span className="text-sm font-medium text-foreground">
              {selectedCount} {selectedCount === 1 ? 'property' : 'properties'} selected
            </span>
            <p className="text-xs text-muted-foreground">
              Choose an action to apply to selected properties
            </p>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <Select
            options={bulkActionOptions}
            value={selectedAction}
            onChange={setSelectedAction}
            placeholder="Select action..."
            className="w-full sm:w-64"
          />

          <div className="flex space-x-2">
            <Button
              variant={getActionVariant(selectedAction)}
              onClick={handleBulkAction}
              disabled={!selectedAction || isProcessing}
              loading={isProcessing}
              iconName={selectedAction ? getActionIcon(selectedAction) : 'Settings'}
              iconPosition="left"
            >
              {isProcessing ? 'Processing...' : 'Apply Action'}
            </Button>

            <Button
              variant="ghost"
              onClick={() => onBulkAction('clear-selection')}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Action Buttons */}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
        <span className="text-sm text-muted-foreground font-medium">Quick Actions:</span>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedAction('schedule-survey');
            handleBulkAction();
          }}
          iconName="Calendar"
          iconPosition="left"
        >
          Schedule Surveys
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedAction('export-data');
            handleBulkAction();
          }}
          iconName="Download"
          iconPosition="left"
        >
          Export Data
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedAction('generate-reports');
            handleBulkAction();
          }}
          iconName="FileText"
          iconPosition="left"
        >
          Generate Reports
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedAction('assign-surveyor');
            handleBulkAction();
          }}
          iconName="UserCheck"
          iconPosition="left"
        >
          Assign Surveyor
        </Button>
      </div>
      {/* Action Confirmation */}
      {selectedAction && (
        <div className="mt-4 p-3 bg-muted rounded-md">
          <div className="flex items-start space-x-2">
            <Icon 
              name={selectedAction === 'delete' ? 'AlertTriangle' : 'Info'} 
              size={16} 
              color={selectedAction === 'delete' ? 'var(--color-error)' : 'var(--color-muted-foreground)'} 
              className="mt-0.5" 
            />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">
                {selectedAction === 'delete' && 'Warning: Permanent Action'}
                {selectedAction === 'archive' && 'Archive Confirmation'}
                {!['delete', 'archive']?.includes(selectedAction) && 'Action Preview'}
              </p>
              <p className="text-muted-foreground">
                {selectedAction === 'schedule-survey' && `Schedule surveys for ${selectedCount} selected properties. You'll be able to set dates and assign surveyors.`}
                {selectedAction === 'update-status' && `Update the status of ${selectedCount} selected properties. Choose from pending, in-progress, completed, or requires update.`}
                {selectedAction === 'assign-surveyor' && `Assign a surveyor to ${selectedCount} selected properties. Choose from available team members.`}
                {selectedAction === 'export-data' && `Export data for ${selectedCount} selected properties in your preferred format (PDF, Excel, CSV).`}
                {selectedAction === 'generate-reports' && `Generate comprehensive reports for ${selectedCount} selected properties including valuations and survey data.`}
                {selectedAction === 'send-notifications' && `Send notifications to clients and team members about ${selectedCount} selected properties.`}
                {selectedAction === 'archive' && `Archive ${selectedCount} selected properties. They will be moved to archived status but can be restored later.`}
                {selectedAction === 'delete' && `Permanently delete ${selectedCount} selected properties. This action cannot be undone and will remove all associated data.`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsToolbar;